import { Injectable } from '@angular/core';

interface PerformanceMetrics {
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  fcp?: number; // First Contentful Paint
  ttfb?: number; // Time to First Byte
  domContentLoaded?: number;
  loadComplete?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PerformanceMonitorService {
  private metrics: PerformanceMetrics = {};
  private isMonitoring = false;

  // Set to true to enable performance monitoring and console logging
  // Currently paused - set to true when ready to resume performance analysis
  private enabled = false;

  constructor() {
    this.initializeMonitoring();
  }

  private initializeMonitoring(): void {
    // Performance monitoring is paused - set enabled = true to resume
    if (!this.enabled || typeof window === 'undefined' || this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;

    // Monitor Core Web Vitals using Performance Observer API
    this.observeLCP();
    this.observeFID();
    this.observeCLS();
    this.observeFCP();
    this.measureNavigationTimings();

    // Log metrics after page load
    if (document.readyState === 'complete') {
      this.reportMetrics();
    } else {
      window.addEventListener('load', () => {
        setTimeout(() => this.reportMetrics(), 1000);
      });
    }
  }

  private observeLCP(): void {
    try {
      const observer = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.metrics.lcp = lastEntry.startTime;
        console.log('🎯 LCP (Largest Contentful Paint):', this.metrics.lcp, 'ms');
      });
      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      console.warn('LCP observation not supported');
    }
  }

  private observeFID(): void {
    try {
      const observer = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry: any) => {
          this.metrics.fid = entry.processingStart - entry.startTime;
          console.log('⚡ FID (First Input Delay):', this.metrics.fid, 'ms');
        });
      });
      observer.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      console.warn('FID observation not supported');
    }
  }

  private observeCLS(): void {
    try {
      let clsValue = 0;
      const observer = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        this.metrics.cls = clsValue;
        console.log('📐 CLS (Cumulative Layout Shift):', this.metrics.cls);
      });
      observer.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      console.warn('CLS observation not supported');
    }
  }

  private observeFCP(): void {
    try {
      const observer = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry) => {
          if (entry.name === 'first-contentful-paint') {
            this.metrics.fcp = entry.startTime;
            console.log('🎨 FCP (First Contentful Paint):', this.metrics.fcp, 'ms');
          }
        });
      });
      observer.observe({ entryTypes: ['paint'] });
    } catch (e) {
      console.warn('FCP observation not supported');
    }
  }

  private measureNavigationTimings(): void {
    if ('performance' in window && 'getEntriesByType' in performance) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
          if (navigation) {
            this.metrics.ttfb = navigation.responseStart - navigation.requestStart;
            this.metrics.domContentLoaded = navigation.domContentLoadedEventEnd - navigation.fetchStart;
            this.metrics.loadComplete = navigation.loadEventEnd - navigation.fetchStart;
            
            console.log('🚀 TTFB (Time to First Byte):', this.metrics.ttfb, 'ms');
            console.log('📄 DOM Content Loaded:', this.metrics.domContentLoaded, 'ms');
            console.log('✅ Load Complete:', this.metrics.loadComplete, 'ms');
          }
        }, 100);
      });
    }
  }

  private reportMetrics(): void {
    console.group('📊 Performance Metrics Summary');
    console.table(this.metrics);
    
    // Performance scoring based on Google's thresholds
    const score = this.calculatePerformanceScore();
    console.log(`🏆 Overall Performance Score: ${score.score}/100 (${score.grade})`);
    
    if (score.recommendations.length > 0) {
      console.log('💡 Recommendations:');
      score.recommendations.forEach(rec => console.log(`  - ${rec}`));
    }
    
    console.groupEnd();

    // Send metrics to analytics (if needed)
    this.sendMetricsToAnalytics();
  }

  private calculatePerformanceScore(): { score: number; grade: string; recommendations: string[] } {
    const recommendations: string[] = [];
    let score = 100;

    // LCP scoring (Good: <2.5s, Needs Improvement: 2.5-4s, Poor: >4s)
    if (this.metrics.lcp !== undefined) {
      if (this.metrics.lcp > 4000) {
        score -= 30;
        recommendations.push('Improve Largest Contentful Paint (>4s is poor)');
      } else if (this.metrics.lcp > 2500) {
        score -= 15;
        recommendations.push('Optimize Largest Contentful Paint (2.5-4s needs improvement)');
      }
    }

    // FID scoring (Good: <100ms, Needs Improvement: 100-300ms, Poor: >300ms)
    if (this.metrics.fid !== undefined) {
      if (this.metrics.fid > 300) {
        score -= 25;
        recommendations.push('Reduce First Input Delay (>300ms is poor)');
      } else if (this.metrics.fid > 100) {
        score -= 10;
        recommendations.push('Optimize First Input Delay (100-300ms needs improvement)');
      }
    }

    // CLS scoring (Good: <0.1, Needs Improvement: 0.1-0.25, Poor: >0.25)
    if (this.metrics.cls !== undefined) {
      if (this.metrics.cls > 0.25) {
        score -= 25;
        recommendations.push('Fix Cumulative Layout Shift (>0.25 is poor)');
      } else if (this.metrics.cls > 0.1) {
        score -= 10;
        recommendations.push('Reduce Cumulative Layout Shift (0.1-0.25 needs improvement)');
      }
    }

    // FCP scoring (Good: <1.8s, Needs Improvement: 1.8-3s, Poor: >3s)
    if (this.metrics.fcp !== undefined) {
      if (this.metrics.fcp > 3000) {
        score -= 15;
        recommendations.push('Improve First Contentful Paint (>3s is poor)');
      } else if (this.metrics.fcp > 1800) {
        score -= 5;
        recommendations.push('Optimize First Contentful Paint (1.8-3s needs improvement)');
      }
    }

    // TTFB scoring (Good: <600ms, Needs Improvement: 600-1s, Poor: >1s)
    if (this.metrics.ttfb !== undefined) {
      if (this.metrics.ttfb > 1000) {
        score -= 10;
        recommendations.push('Improve Time to First Byte (>1s is poor)');
      } else if (this.metrics.ttfb > 600) {
        score -= 5;
        recommendations.push('Optimize Time to First Byte (600ms-1s needs improvement)');
      }
    }

    const grade = score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F';
    
    return { score: Math.max(0, score), grade, recommendations };
  }

  private sendMetricsToAnalytics(): void {
    // Example implementation - replace with your analytics service
    if (typeof (window as any).gtag !== 'undefined' && this.metrics.lcp && this.metrics.fcp) {
      (window as any).gtag('event', 'web_vitals', {
        custom_parameter_lcp: Math.round(this.metrics.lcp),
        custom_parameter_fid: Math.round(this.metrics.fid || 0),
        custom_parameter_cls: Math.round((this.metrics.cls || 0) * 1000) / 1000,
        custom_parameter_fcp: Math.round(this.metrics.fcp),
      });
    }
  }

  // Public method to get current metrics
  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  // Public method to get performance score
  public getPerformanceScore() {
    return this.calculatePerformanceScore();
  }
}